import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CalendarBlank, MapPin, Clock, CheckCircle, Confetti, Ticket } from "@phosphor-icons/react";
import { PageLoader, Logo } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import api, { apiErr, API } from "../lib/api";

function fmtDate(d) {
  if (!d) return "TBA";
  try { return new Date(d).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); }
  catch { return d; }
}

const STATUS_LABELS = {
  attending: "Yes, I'll be there", maybe: "Maybe", not_sure: "Not sure yet", not_attending: "Can't make it",
  confirmed: "Yes, I'll attend", declined: "Sorry, I can't make it",
};

export default function RsvpResponse() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState("");
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/rsvp/${token}`).then((r) => {
      setData(r.data);
      const q = {};
      (r.data.questions || []).forEach((qn) => { if (qn.id === "party_size") q[qn.id] = 1; else if (qn.type === "boolean") q[qn.id] = false; else q[qn.id] = ""; });
      setAnswers(q);
      if (r.data.guest?.response) { setStatus(r.data.guest.response.status); setAnswers((a) => ({ ...a, ...(r.data.guest.response.custom_answers || {}) })); }
      if (r.data.ticket) setDone({ ticket: r.data.ticket, message: "You're all set — here's your entry pass." });
    }).catch((e) => setErr(apiErr(e))).finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    if (!status) { toast.error("Please choose a response."); return; }
    setSaving(true);
    try {
      const { data: res } = await api.post(`/rsvp/${token}`, { status, custom_answers: answers });
      setDone(res);
      toast.success("Response recorded. Thank you!");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  if (loading) return <PageLoader label="Loading your invitation" />;
  if (err) return (
    <div className="min-h-screen grid place-items-center px-4">
      <div className="text-center max-w-sm"><Logo /><p className="text-muted-foreground mt-4">{err}</p></div>
    </div>
  );

  const { event, occurrence, guest, questions = [], statuses = [], template = {}, has_tickets } = data;
  const attending = status === "attending" || status === "confirmed";

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="h-40 md:h-56 relative overflow-hidden">
        {event.banner && <img src={event.banner} alt={event.title} className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
        <div className="absolute top-5 left-5"><Logo dark /></div>
      </div>
      <div className="mx-auto max-w-lg px-4 -mt-14 relative z-10 pb-16">
        <div className="rounded-2xl border border-border bg-card p-6 shadow-lg">
          <div className="overline mb-1 text-primary">You're invited</div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight" data-testid="rsvp-event-title">{event.title}</h1>
          <div className="mt-3 space-y-1.5 text-sm text-muted-foreground">
            <div className="flex items-center gap-2"><CalendarBlank size={16} className="text-primary" /> {fmtDate(occurrence.date)}</div>
            <div className="flex items-center gap-2"><Clock size={16} className="text-primary" /> {occurrence.time || "TBA"}</div>
            <div className="flex items-center gap-2"><MapPin size={16} className="text-primary" /> {occurrence.venue}, {occurrence.city}</div>
          </div>
          {template.message && <p className="mt-4 text-sm text-foreground/80 border-l-2 border-primary/40 pl-3">{template.message.replace("{name}", guest.name)}</p>}

          {done ? (
            <div className="mt-6 rounded-xl bg-emerald-50 border border-emerald-200 p-5 text-center" data-testid="rsvp-done">
              <CheckCircle size={40} weight="fill" className="text-emerald-600 mx-auto" />
              <p className="font-heading text-lg font-semibold mt-2">{done.message}</p>
              {done.ticket && (
                <div className="mt-4 rounded-xl bg-white border border-border p-4" data-testid="rsvp-ticket">
                  <div className="text-xs uppercase tracking-widest text-primary font-bold">{done.ticket.category || "Entry Pass"}</div>
                  <img src={done.ticket.qr} alt="QR pass" className="w-44 h-44 mx-auto mt-2" />
                  <div className="font-mono text-sm mt-1">{done.ticket.code}</div>
                  <Button variant="outline" className="rounded-full mt-3" data-testid="download-pass"
                    onClick={() => window.open(`${API}/rsvp/${token}/ticket.pdf`, "_blank")}>
                    <Ticket className="mr-1.5" /> Download / print pass
                  </Button>
                  {done.ticket.map_link && (
                    <a href={done.ticket.map_link} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1 mt-3 text-sm text-primary font-semibold">
                      <MapPin size={14} /> Get directions
                    </a>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">Show this QR at the entry gate.</p>
                </div>
              )}
              {done.allow_ticket && done.invite_token && (
                <Button className="rounded-full mt-4" data-testid="rsvp-claim-ticket"
                  onClick={() => navigate(`/events/${done.event_id}?invite=${done.invite_token}`)}>
                  <Ticket weight="fill" className="mr-1.5" /> Complete payment to get your ticket
                </Button>
              )}
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              <div>
                <Label className="mb-2 block">Hi {guest.name}, will you attend?</Label>
                <div className="grid gap-2">
                  {statuses.map((s) => (
                    <button key={s} onClick={() => setStatus(s)} data-testid={`rsvp-status-${s}`}
                      className={`w-full text-left px-4 h-11 rounded-lg border font-medium text-sm transition-colors ${status === s ? "border-primary bg-primary/5 text-foreground" : "border-border hover:border-foreground/30 text-muted-foreground"}`}>
                      {STATUS_LABELS[s] || s}
                    </button>
                  ))}
                </div>
              </div>

              {attending && questions.map((qn) => (
                <div key={qn.id} data-testid={`rsvp-q-${qn.id}`}>
                  <Label className="mb-1.5 block">{qn.label}{qn.required && <span className="text-primary"> *</span>}</Label>
                  {qn.type === "number" ? (
                    <Input type="number" min={1} value={answers[qn.id] ?? ""} onChange={(e) => setAnswers((a) => ({ ...a, [qn.id]: +e.target.value }))} className="h-11" />
                  ) : qn.type === "select" ? (
                    <Select value={String(answers[qn.id] ?? "")} onValueChange={(v) => setAnswers((a) => ({ ...a, [qn.id]: v }))}>
                      <SelectTrigger className="h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{(qn.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : qn.type === "boolean" ? (
                    <label className="flex items-center justify-between p-3 rounded-lg border border-border">
                      <span className="text-sm text-muted-foreground">Yes / No</span>
                      <Switch checked={!!answers[qn.id]} onCheckedChange={(v) => setAnswers((a) => ({ ...a, [qn.id]: v }))} />
                    </label>
                  ) : (
                    <Input value={answers[qn.id] ?? ""} onChange={(e) => setAnswers((a) => ({ ...a, [qn.id]: e.target.value }))} className="h-11" />
                  )}
                </div>
              ))}

              <Button onClick={submit} disabled={saving || !status} className="w-full h-12 rounded-full text-base" data-testid="rsvp-submit">
                {saving ? "Sending…" : <><Confetti weight="fill" className="mr-2" /> Send my response</>}
              </Button>
              {has_tickets && attending && <p className="text-xs text-center text-muted-foreground">Confirm to unlock your ticket for this invite-only event.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
